import './index.less';

import {
  Box,
  Divider,
  Flex,
  OverflowTooltip,
  Text,
  useColorMode,
  useColorStyle,
} from '@tonic-one/react';
import { useTranslation } from 'react-i18next';

import iconBec from '../../../assets/image/icon-bec.svg';
import iconMaliciousFile from '../../../assets/image/icon-malicious-file.svg';
import iconMaliciousUrl from '../../../assets/image/icon-malicious-url.svg';
import iconPhishing from '../../../assets/image/icon-phishing.svg';
import iconRansomware from '../../../assets/image/icon-ransomware.svg';
import iconSpam from '../../../assets/image/icon-spam.svg';
import DataTrack from '../../../components/DataTrack';
import { reportPendo } from '../../../utils/setupPendo';
import { Report, ThreatType } from './report';

const ReportHeader = (props: {
  report: Report | null;
  services: string[];
  threatType: ThreatType;
  onThreatType: (threatType: ThreatType) => void;
}) => {
  const { t } = useTranslation();
  const [colorMode] = useColorMode();

  const bec = computeThreatSummary(
    props.report,
    props.services,
    ThreatType.BEC
  );
  const phishing = computeThreatSummary(
    props.report,
    props.services,
    ThreatType.Phishing
  );
  const ransomware = computeThreatSummary(
    props.report,
    props.services,
    ThreatType.Ransomware
  );
  const maliciousFile = computeThreatSummary(
    props.report,
    props.services,
    ThreatType.MaliciousFile
  );
  const maliciousURL = computeThreatSummary(
    props.report,
    props.services,
    ThreatType.MaliciousURL
  );
  const spam = computeThreatSummary(
    props.report,
    props.services,
    ThreatType.Spam
  );
  reportPendo(`ConfigRecommendation.FeaturesNotAdopted.Summary`, {
    bec: bec.illegal,
    phishing: phishing.illegal,
    ransomware: ransomware.illegal,
    maliciousFile: maliciousFile.illegal,
    maliciousURL: maliciousURL.illegal,
    spam: spam.illegal,
  });

  return (
    <Box>
      <Flex direction="row">
        <ThreatItem
          threatType={ThreatType.BEC}
          icon={
            <img
              src={iconBec}
              width="20px"
              style={{ filter: colorMode === 'dark' ? undefined : 'invert(1)' }}
            />
          }
          label={t('recommendConfig.threat.bec')}
          selected={props.threatType === ThreatType.BEC}
          onClick={props.onThreatType.bind(null, ThreatType.BEC)}
          summary={bec}
        />
        <Divider orientation="vertical" m="12px 0" />
        <ThreatItem
          threatType={ThreatType.Phishing}
          icon={
            <img
              src={iconPhishing}
              width="20px"
              style={{ filter: colorMode === 'dark' ? undefined : 'invert(1)' }}
            />
          }
          label={t('recommendConfig.threat.phishing')}
          selected={props.threatType === ThreatType.Phishing}
          onClick={props.onThreatType.bind(null, ThreatType.Phishing)}
          summary={phishing}
        />
        <Divider orientation="vertical" m="12px 0" />
        <ThreatItem
          threatType={ThreatType.Ransomware}
          icon={
            <img
              src={iconRansomware}
              width="20px"
              style={{ filter: colorMode === 'dark' ? undefined : 'invert(1)' }}
            />
          }
          label={t('recommendConfig.threat.ransomware')}
          selected={props.threatType === ThreatType.Ransomware}
          onClick={props.onThreatType.bind(null, ThreatType.Ransomware)}
          summary={ransomware}
        />
        <Divider orientation="vertical" m="12px 0" />
        <ThreatItem
          threatType={ThreatType.MaliciousFile}
          icon={
            <img
              src={iconMaliciousFile}
              width="20px"
              style={{ filter: colorMode === 'dark' ? undefined : 'invert(1)' }}
            />
          }
          label={t('recommendConfig.threat.maliciousFile')}
          selected={props.threatType === ThreatType.MaliciousFile}
          onClick={props.onThreatType.bind(null, ThreatType.MaliciousFile)}
          summary={maliciousFile}
        />
        <Divider orientation="vertical" m="12px 0" />
        <ThreatItem
          threatType={ThreatType.MaliciousURL}
          icon={
            <img
              src={iconMaliciousUrl}
              width="20px"
              style={{ filter: colorMode === 'dark' ? undefined : 'invert(1)' }}
            />
          }
          label={t('recommendConfig.threat.maliciousUrl')}
          selected={props.threatType === ThreatType.MaliciousURL}
          onClick={props.onThreatType.bind(null, ThreatType.MaliciousURL)}
          summary={maliciousURL}
        />
        <Divider orientation="vertical" m="12px 0" />
        <ThreatItem
          threatType={ThreatType.Spam}
          icon={
            <img
              src={iconSpam}
              width="20px"
              style={{ filter: colorMode === 'dark' ? undefined : 'invert(1)' }}
            />
          }
          label={t('recommendConfig.threat.spam')}
          selected={props.threatType === ThreatType.Spam}
          onClick={props.onThreatType.bind(null, ThreatType.Spam)}
          summary={spam}
        />
      </Flex>
      <Divider orientation="horizontal" position="relative" top="-2px" />
    </Box>
  );
};

const ThreatItem = (props: {
  threatType: ThreatType;
  icon: JSX.Element;
  label: string;
  selected?: boolean;
  onClick?: () => void;
  summary: {
    illegal: number | null;
    level: string | null;
  };
}) => {
  const [colorMode] = useColorMode();
  const [colorStyle] = useColorStyle({ colorMode });
  const { t } = useTranslation();

  let label = null;
  if (props.summary.level === 'high') {
    label = (
      <Text display="inline" color="#04C45A">
        {t('recommendConfig.filter.good')}
      </Text>
    );
  } else if (props.summary.level === 'medium') {
    label = (
      <Text display="inline" color="#F9B92A">
        {t('recommendConfig.filter.partial')}
      </Text>
    );
  } else if (props.summary.level === 'low') {
    label = (
      <Text display="inline" color="#F24C4F">
        {t('recommendConfig.filter.warning')}
      </Text>
    );
  } else {
    label = <Text display="inline">N/A</Text>;
  }

  let className;
  if (colorMode === 'dark') {
    className = props.selected ? 'dark-threat dark-selected' : 'dark-threat';
  } else {
    className = props.selected ? 'light-threat light-selected' : 'light-threat';
  }

  const featuresNotAdoptedString = `${t(
    'recommendConfig.filter.featuresNotAdopted'
  )}: ${
    typeof props.summary.illegal === 'number' ? props.summary.illegal : 'N/A'
  }`;

  return (
    <DataTrack
      type={`ConfigRecommendation.FeatureAdoption.Header.${props.threatType}.Click`}
      metadata={{ illegal: props.summary.illegal }}
    >
      <Box
        width="calc((100% - 5px) / 6)"
        p="12px 16px 7px"
        className={className}
        cursor="pointer"
        color={colorStyle.color.primary}
        onClick={props.onClick}
      >
        <Flex direction="row" justify="flex-start" mb="8px">
          {props.icon}
          <Text
            ml="10px"
            fontSize="16px"
            fontWeight="600"
            overflow="hidden"
            whiteSpace="nowrap"
            textOverflow="ellipsis"
          >
            {props.label}
          </Text>
        </Flex>
        <OverflowTooltip label={featuresNotAdoptedString}>
          {featuresNotAdoptedString}
        </OverflowTooltip>
        <Flex
          direction="row"
          align="center"
          justify="flex-start"
          mt="18px"
          fontSize="12px"
          color={colorStyle.color.secondary}
          overflow="hidden"
          whiteSpace="nowrap"
          textOverflow="ellipsis"
        >
          {t('recommendConfig.filter.adoption')}
          :&nbsp;
          {label}
        </Flex>
      </Box>
    </DataTrack>
  );
};

function computeThreatSummary(
  report: Report | null,
  services: string[],
  threatType: ThreatType
) {
  if (!report || services.length === 0) {
    return { illegal: null, level: null };
  }

  let illegal = 0;
  let protection = 0;

  for (const service of services) {
    const threatReport = report?.[service]?.[threatType];
    if (!threatReport) {
      protection = 30;
      continue;
    }

    for (const item of threatReport) {
      if (!item.legal) {
        illegal += 1;
        protection = Math.max(protection, item.protection);
      }
    }
  }

  let level;
  if (protection <= 10) {
    level = 'high';
  } else if (protection <= 20) {
    level = 'medium';
  } else {
    level = 'low';
  }

  return { illegal, level };
}

export default ReportHeader;
